import { useId, useState, type FormEvent } from "react";
import { Link, useParams } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import { isConflict, isNotFound, isUnauthorized } from "../../api/errors";
import { useAgentSession, useCorpusBinding, useProject, useRunAgentSessionTask } from "../../api/queries";
import { DirtyBadge, StatusBadge } from "../bonnie/BonnieSessionsPage";
import { Banner } from "../../components/Banner";
import { Breadcrumb } from "../../components/Breadcrumb";
import { Button } from "../../components/Button";
import { ErrorState, LoadingState } from "../../components/AsyncStates";
import { FormError } from "../../components/FormError";
import { ProposeDevelopDialog } from "./ProposeDevelopDialog";
import "../../components/Form.css";
import "../bonnie/bonnie.css";
import "./develop.css";

/** One instruction the operator sent in this browser tab, kept ONLY for the
 * lifetime of this page view. This is not a cache of a server fact (A-5):
 * there is no server-side transcript to drift from — `RunTask` is fire-
 * and-forget (`agentsession.Service.RunTask`, 202-Accepted) with no
 * response body beyond "accepted," so the only honest record of "what was
 * asked" is what THIS tab actually sent. Reloading the page loses it, same
 * as reloading loses an un-sent draft in any composer — never presented as
 * more durable than it is. */
interface SentPrompt {
  id: string;
  prompt: string;
  sentAt: string;
}

/**
 * The Develop conversational authoring view (ADR-0052 M6 §2.2): send the
 * develop agent instructions and watch its real, server-reported progress
 * (status/branch/worktree/what-changed) as it drafts the project's corpus on
 * its own machine — then propose the result as a pull request.
 *
 * What this deliberately does NOT render: turn-by-turn assistant text or
 * `validate` tool output. That live feed exists only INSIDE the daemon
 * process today (`agent/bonnie/devlayer/turnstream.go`'s `turnBroker` — read
 * its package doc: "the REMOTE path... is DEFERRED"). There is no control-
 * plane route or web transport that relays it to a browser, so rendering a
 * "live drafting" panel here would be exactly the lying/inert surface A-10
 * forbids — a panel that looks connected but never receives anything. This
 * page instead shows only what the control plane actually reports: session
 * status and the worktree's branch/dirty/changeSummary (the same real,
 * polled signal `BonnieSessionsPage` uses), refreshed automatically. See
 * this feature's build report for the follow-up (a CP relay of the turn
 * stream) needed to make live drafting text possible here.
 */
export function DevelopSessionPage() {
  const { projectId, sessionId } = useParams<{ projectId: string; sessionId: string }>();
  const { session: authSession } = useAuth();
  const canManage = canManageFleet(authSession?.role);
  const promptId = useId();
  const [prompt, setPrompt] = useState("");
  const [sent, setSent] = useState<SentPrompt[]>([]);
  const [proposeOpen, setProposeOpen] = useState(false);

  const {
    data: project,
    isPending: projectPending,
    isError: projectIsError,
    error: projectError,
    refetch: refetchProject,
  } = useProject(projectId);

  const {
    data: agentSession,
    isPending: sessionPending,
    isError: sessionIsError,
    error: sessionError,
    refetch: refetchSession,
  } = useAgentSession(sessionId);

  const {
    data: binding,
    isError: bindingIsError,
    error: bindingError,
  } = useCorpusBinding(projectId ?? "", { enabled: canManage });
  const bindingConfirmedMissing = bindingIsError && isNotFound(bindingError);

  const taskMutation = useRunAgentSessionTask();

  if (projectPending || sessionPending) {
    return <LoadingState label="Loading session…" />;
  }

  if (projectIsError) {
    if (isUnauthorized(projectError)) return <LoadingState label="Loading session…" />;
    if (isNotFound(projectError)) {
      return (
        <ErrorState
          title="Project not found"
          description="It may have been removed, or the link is out of date. Head back to the projects list."
          action={
            <Link to="/projects" className="button button--secondary">
              Back to Develop
            </Link>
          }
        />
      );
    }
    return (
      <ErrorState
        description={projectError instanceof Error ? projectError.message : "Couldn't load this project."}
        onRetry={refetchProject}
      />
    );
  }

  if (sessionIsError) {
    if (isUnauthorized(sessionError)) return <LoadingState label="Loading session…" />;
    if (isNotFound(sessionError)) {
      return (
        <ErrorState
          title="Session not found"
          description="This develop session may have been stopped and deleted. Start a new one from the project's Sessions tab."
          action={
            <Link to={`/projects/${projectId}/sessions`} className="button button--secondary">
              Go to Sessions
            </Link>
          }
        />
      );
    }
    return (
      <ErrorState
        description={sessionError instanceof Error ? sessionError.message : "Couldn't load this session."}
        onRetry={refetchSession}
      />
    );
  }

  if (!project || !agentSession) return null;

  function handleSend(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!sessionId) return;
    const submitted = prompt.trim();
    if (!submitted) return;
    taskMutation.mutate(
      { id: sessionId, input: { prompt: submitted } },
      {
        onSuccess: () => {
          setSent((prev) => [...prev, { id: `${Date.now()}`, prompt: submitted, sentAt: new Date().toISOString() }]);
          setPrompt("");
        },
      },
    );
  }

  return (
    <div className="develop-page">
      <Breadcrumb
        items={[
          { label: "Develop", to: "/projects" },
          { label: project.name, to: `/projects/${project.id}` },
          { label: "Sessions", to: `/projects/${project.id}/sessions` },
          { label: agentSession.label },
        ]}
      />

      <div className="develop-page__header">
        <div>
          <h1 className="develop-page__title">Develop: {agentSession.label}</h1>
          <p className="develop-page__subtitle">
            A design conversation, co-authoring {project.name}'s corpus. Send instructions below;
            nothing merges until you propose and review a pull request.
          </p>
        </div>
        {canManage && (
          <Button variant="primary" onClick={() => setProposeOpen(true)} disabled={!agentSession.branch}>
            Propose
          </Button>
        )}
      </div>

      <section className="develop-status" aria-label="Session status">
        <StatusBadge status={agentSession.status} />
        {agentSession.branch && <code className="bonnie-branch">{agentSession.branch}</code>}
        <DirtyBadge session={agentSession} />
        {agentSession.changeSummary && (
          <span className="develop-status__summary">{agentSession.changeSummary}</span>
        )}
      </section>

      {!canManage && (
        <Banner tone="info" title="View only" className="develop-dialog-banner">
          Your role can view this session but not drive it — an operator or admin can send
          instructions and propose its work.
        </Banner>
      )}

      <Banner tone="info" title="No live drafting feed yet" className="develop-dialog-banner">
        Comebye doesn't yet relay the agent's turn-by-turn drafting to the browser — send an
        instruction below, then watch the worktree status above (it refreshes automatically) for
        what changed.
      </Banner>

      {canManage && (
        <section className="develop-composer" aria-label="Send an instruction">
          {agentSession.status !== "running" && (
            <Banner tone="warning" title="Session isn't running" className="develop-dialog-banner">
              Only a running session can accept an instruction. Wait for it to start, or check the
              Corpus page's binding health if it keeps failing.
            </Banner>
          )}
          <form onSubmit={handleSend} noValidate>
            <label className="form-field" htmlFor={promptId}>
              <span>Tell it what to develop</span>
              <textarea
                id={promptId}
                rows={4}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                required
                maxLength={8000}
                placeholder="e.g. We're building a loyalty points app for small retailers. Draft the vision and a first roadmap step."
              />
            </label>

            {taskMutation.isError && taskMutation.error && (() => {
              const err = taskMutation.error;
              const message = err.message || "Couldn't send that instruction.";
              return isConflict(err) ? (
                <Banner tone="warning" title="Session not running" className="develop-dialog-banner">
                  {message}
                </Banner>
              ) : (
                <FormError>{message}</FormError>
              );
            })()}

            <div className="form-actions">
              <Button type="submit" variant="primary" disabled={taskMutation.isPending || !prompt.trim()}>
                {taskMutation.isPending ? "Sending…" : "Send"}
              </Button>
            </div>
          </form>
        </section>
      )}

      <section className="develop-transcript" aria-label="What you've sent">
        <h2>Sent this session</h2>
        {sent.length === 0 ? (
          <p className="develop-transcript__empty">Nothing sent yet in this browser tab.</p>
        ) : (
          <ol className="develop-transcript__list">
            {sent
              .slice()
              .reverse()
              .map((item) => (
                <li key={item.id} className="develop-transcript__item">
                  <time dateTime={item.sentAt}>{new Date(item.sentAt).toLocaleTimeString()}</time>
                  <p>{item.prompt}</p>
                </li>
              ))}
          </ol>
        )}
      </section>

      {canManage && (
        <ProposeDevelopDialog
          session={proposeOpen ? agentSession : null}
          projectId={project.id}
          projectName={project.name}
          binding={binding}
          bindingConfirmedMissing={Boolean(bindingConfirmedMissing)}
          onClose={() => setProposeOpen(false)}
        />
      )}
    </div>
  );
}
